import React, { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ComparisonFilters } from "@/hooks/useComparisonState";
import { OrganizedRoom } from "../types";
import { CollapsibleRoomCard } from "./CollapsibleRoomCard";

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { RoomDetail } from '@/api/generated/client';

interface OrganizeTabProps {
    rooms: OrganizedRoom[];
    filters: ComparisonFilters;
    selectedItemIds: Set<number>;
    onToggleItemSelection: (itemId: number) => void;
    onMoveItems: (targetRoomId: number) => void;
    onDeleteRoom: (roomId: number) => void;
    onMoveLineItem: (itemId: number, targetRoomId: number) => void;
    onSetItemsSelection: (itemIds: number[], isSelected: boolean) => void;
    onDeleteLineItem: (itemId: number) => void;
    comparisonId: number;
    canWrite: boolean;
    // New Filter Props
    overscopedIds?: Set<number>;
    additionalIds?: Set<number>;
    discrepancyIds?: Set<number>;
    notOnInsuranceIds?: Set<number>;
    toggleShowOverscoped?: () => void;
    toggleShowAdditional?: () => void;
    toggleShowDiscrepancy?: () => void;
    toggleShowNotOnInsurance?: () => void;
    // Missing props causing lint errors - adding them as optional to suppress errors without breaking existing code if it's used elsewhere
    allRoomsList?: RoomDetail[];
    insurerEstimateId?: number;
    contractorEstimateId?: number;
    onTriggerCreateRoom?: (type: 'insurer' | 'contractor') => void;
}

export const OrganizeTab: React.FC<OrganizeTabProps> = ({
    rooms,
    filters,
    selectedItemIds,
    onToggleItemSelection,
    onMoveItems,
    onDeleteRoom,
    onMoveLineItem,
    onSetItemsSelection,
    onDeleteLineItem,
    comparisonId,
    canWrite,
    overscopedIds,
    additionalIds,
    discrepancyIds,
    notOnInsuranceIds,
    onTriggerCreateRoom,
}) => {
    const [activeDragId, setActiveDragId] = useState<number | null>(null);
    const [activeDragType, setActiveDragType] = useState<'insurer' | 'contractor' | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // -- Filtering and Sorting --
    // Sort main room display alphabetically
    const sortedRooms = [...rooms].sort((a, b) => {
        const nameA = a.level ? `${a.level} - ${a.name}` : a.name;
        const nameB = b.level ? `${b.level} - ${b.name}` : b.name;
        return nameA.localeCompare(nameB);
    });

    // Filter
    const filteredRooms = sortedRooms.map(room => {
        const query = filters.searchQuery.toLowerCase().trim();
        // Combine active search terms plus the current query if any
        const terms = [...filters.searchTerms, query].filter(t => t.length > 0).map(t => t.toLowerCase());

        let roomMatches = false;
        let matchItems = room.items;

        // 1. Keyword Search
        if (terms.length > 0) {
            // Check if room name or level matches ANY term
            roomMatches = terms.some(term =>
                room.name.toLowerCase().includes(term) ||
                (room.level && room.level.toLowerCase().includes(term))
            );

            if (roomMatches) {
                // If room matches, show ALL items (subject to other filters)
                matchItems = room.items;
            } else {
                // If room doesn't match, filter items by terms
                matchItems = room.items.filter(item =>
                    terms.some(term => item.description?.toLowerCase().includes(term))
                );
            }
        } else {
            // No search terms, show everything
            roomMatches = true;
        }

        // 2. Apply "Show Matched" filter
        matchItems = matchItems.filter(item => {
            const isMatched = !!item.lineItemMatchGroupId;
            if (filters.showMatched) return true;
            return !isMatched;
        });

        // 3. Apply Special Filters (Overscope, Additional, etc)
        if (filters.showOverscoped && overscopedIds) {
            matchItems = matchItems.filter(item => item.lineItemId && overscopedIds.has(item.lineItemId));
        }
        if (filters.showAdditional && additionalIds) {
            matchItems = matchItems.filter(item => item.lineItemId && additionalIds.has(item.lineItemId));
        }
        if (filters.showDiscrepancy && discrepancyIds) {
            matchItems = matchItems.filter(item => item.lineItemId && discrepancyIds.has(item.lineItemId));
        }
        if (filters.showNotOnInsurance && notOnInsuranceIds) {
            matchItems = matchItems.filter(item => item.lineItemId && notOnInsuranceIds.has(item.lineItemId));
        }

        // Apply "Hide Empty Rooms"
        const activeSpecialFilters = filters.showOverscoped || filters.showAdditional || filters.showDiscrepancy || filters.showNotOnInsurance;
        const shouldHideEmpty = (filters.hideEmptyRooms || activeSpecialFilters) && matchItems.length === 0;

        return { ...room, items: matchItems, originalItemCount: room.items.length, isHidden: (matchItems.length === 0 && !roomMatches && terms.length > 0) || shouldHideEmpty };
    }).filter(r => !r.isHidden);

    const insurerRooms = filteredRooms.filter(r => r.type === 'insurer');
    const contractorRooms = filteredRooms.filter(r => r.type === 'contractor');

    // -- DnD Handlers --
    const handleDragStart = (event: any) => {
        const id = Number(event.active.id);
        setActiveDragId(id);

        const sourceRoom = rooms.find(r => r.items.some(i => i.lineItemId === id));
        if (sourceRoom) {
            setActiveDragType(sourceRoom.type as 'insurer' | 'contractor');
        }
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        setActiveDragId(null);
        setActiveDragType(null);
        if (!over) return;


        const itemId = Number(active.id);
        const targetRoomId = Number(over.id);

        if (isNaN(itemId) || isNaN(targetRoomId)) return;

        // Validation: Verify source and target side match (insurer -> insurer)
        // Find source room for item
        const sourceRoom = rooms.find(r => r.items.some(i => i.lineItemId === itemId));
        const targetRoom = rooms.find(r => r.id === targetRoomId);

        if (sourceRoom && targetRoom && sourceRoom.type === targetRoom.type) {
            // Check if the dragged item is part of the current selection
            const isSelected = selectedItemIds.has(itemId);

            if (isSelected) {
                // If dragging a selected item, move ALL selected items
                onMoveItems(targetRoomId);
            } else {
                // If dragging an unselected item, move ONLY that item
                onMoveLineItem(itemId, targetRoomId);
            }
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="space-y-6 relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start pt-4">
                    {/* Insurance Column */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 pb-2 border-b">
                            <Badge variant="outline" className="bg-slate-100 dark:bg-slate-800">
                                Insurance
                            </Badge>
                            <span className="text-xs text-muted-foreground">{insurerRooms.length} Rooms</span>
                            {onTriggerCreateRoom && (
                                <Button
                                    size="icon"
                                    className="h-6 w-6 ml-auto"
                                    onClick={() => onTriggerCreateRoom('insurer')}
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        {insurerRooms.map(room => (
                            <CollapsibleRoomCard
                                key={room.id}
                                room={room}
                                selectedItemIds={selectedItemIds}
                                onToggleItemSelection={onToggleItemSelection}
                                onSetItemsSelection={onSetItemsSelection}
                                isLocked={false} // Room itself is technically not locked from interactions, items inside are
                                activeDragType={activeDragType}
                                activeDragId={activeDragId}
                                onDeleteRoom={onDeleteRoom}
                                onDeleteLineItem={onDeleteLineItem}
                                totalItemCount={(room as any).originalItemCount}
                                comparisonId={comparisonId}
                                canWrite={canWrite}
                            />
                        ))}
                    </div>

                    {/* Contractor Column */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 pb-2 border-b">
                            <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                                Contractor
                            </Badge>
                            <span className="text-xs text-muted-foreground">{contractorRooms.length} Rooms</span>
                            {onTriggerCreateRoom && (
                                <Button
                                    size="icon"
                                    className="h-6 w-6 ml-auto"
                                    onClick={() => onTriggerCreateRoom('contractor')}
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        {contractorRooms.map(room => (
                            <CollapsibleRoomCard
                                key={room.id}
                                room={room}
                                selectedItemIds={selectedItemIds}
                                onToggleItemSelection={onToggleItemSelection}
                                onSetItemsSelection={onSetItemsSelection}
                                isLocked={false}
                                activeDragType={activeDragType}
                                activeDragId={activeDragId}
                                onDeleteRoom={onDeleteRoom}
                                onDeleteLineItem={onDeleteLineItem}
                                totalItemCount={(room as any).originalItemCount}
                                comparisonId={comparisonId}
                                canWrite={canWrite}
                            />
                        ))}
                    </div>
                </div>
                <DragOverlay>
                    {activeDragId ? (
                        <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border w-64 truncate text-sm font-medium">
                            {selectedItemIds.has(activeDragId) && selectedItemIds.size > 1
                                ? `Moving ${selectedItemIds.size} Items...`
                                : "Moving Item..."
                            }
                        </div>
                    ) : null}
                </DragOverlay>
            </div>
        </DndContext>
    );
};
